import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/articles/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(setArticle)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#0A1128] mb-4">Article Not Found</h1>
          <Link to="/" className="text-sm uppercase tracking-widest text-[#6B7280] hover:text-[#0A1128]">Go Home</Link>
        </div>
      </div>
    );
  }

  const renderBlock = (block, index) => {
    const isEven = index % 2 === 0;

    switch (block.block_type) {
      case 'title':
        return (
          <div key={block.block_id} className="max-w-3xl mx-auto mb-12" data-testid={`block-title-${index}`}>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#0A1128] tracking-tight">
              {block.content}
            </h2>
          </div>
        );

      case 'text':
        return (
          <div key={block.block_id} className="max-w-3xl mx-auto mb-12" data-testid={`block-text-${index}`}>
            <p className="text-[#6B7280] leading-relaxed whitespace-pre-line">{block.content}</p>
          </div>
        );

      case 'image':
        return (
          <div key={block.block_id} className="max-w-4xl mx-auto mb-12" data-testid={`block-image-${index}`}>
            <div className={`grid ${block.content ? 'md:grid-cols-2' : 'grid-cols-1'} gap-8 items-center`}>
              <div className={`img-hover-zoom ${!isEven && block.content ? 'md:order-2' : ''}`}>
                <img
                  src={block.image_url || ''}
                  alt={block.content || 'Product image'}
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              {block.content && (
                <div className={!isEven ? 'md:order-1' : ''}>
                  <p className="text-[#6B7280] leading-relaxed">{block.content}</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'affiliate':
        return (
          <div key={block.block_id} className="max-w-4xl mx-auto mb-16" data-testid={`block-affiliate-${index}`}>
            <div className="border border-[#E5E7EB] p-8 md:p-12">
              <div className={`grid ${block.image_url ? 'md:grid-cols-2' : 'grid-cols-1'} gap-8 items-center`}>
                {block.image_url && (
                  <div className={`img-hover-zoom ${!isEven ? 'md:order-2' : ''}`}>
                    <img
                      src={block.image_url}
                      alt={block.content || 'Product'}
                      className="w-full aspect-[4/3] object-cover"
                    />
                  </div>
                )}
                <div className={!isEven && block.image_url ? 'md:order-1' : ''}>
                  {block.content && (
                    <p className="text-[#0A1128] leading-relaxed mb-6">{block.content}</p>
                  )}
                  {block.affiliate_url && (
                    <a
                      href={block.affiliate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`affiliate-link-${index}`}
                    >
                      <Button className="bg-[#0A1128] text-white hover:bg-[#0A1128]/90 rounded-none uppercase tracking-widest text-sm px-8 py-3 inline-flex items-center gap-2">
                        Shop the Look <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-16" data-testid="article-detail-page">
      {/* Article Header */}
      <section className="relative h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.cover_image || 'https://images.unsplash.com/photo-1638972691611-69633a3d3127?q=85'}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-16 w-full">
          <Link
            to={article.category ? `/articles/${article.category}` : '/'}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs uppercase tracking-widest mb-6 transition-colors"
            data-testid="article-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to {article.category?.replace('-', ' ') || 'articles'}
          </Link>
          <p className="overline text-white/50 mb-4">{article.category?.replace('-', ' ')}</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-none">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="px-6 md:px-12">
          {article.blocks?.sort((a, b) => a.position - b.position).map(renderBlock)}
        </div>
      </section>
    </div>
  );
}
